import { useCallback } from "react"
import { useMutation } from "urql"

import "../graphql/mutations"

import type { UpdateAccountingSettingsInput } from "@/graphql/codegen/graphql"
import { UpdateAccountingSettingsDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseUpdateAccountingSettingsReturn {
  updateAccountingSettings: (input: UpdateAccountingSettingsInput) => Promise<boolean>
  isUpdating: boolean
}

export const useUpdateAccountingSettings = (): UseUpdateAccountingSettingsReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation(UpdateAccountingSettingsDocument)

  const updateAccountingSettings = useCallback(
    async (input: UpdateAccountingSettingsInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.accountingSettings.update
      const hasError = error || output?.__typename === "UpdateAccountingSettingsFailure"

      if (hasError) {
        const message = output?.__typename === "UpdateAccountingSettingsFailure" ? output.error.message : error?.message
        notify({
          type: NotificationType.Error,
          message: message ?? "Failed to save Accounting Settings. Please try again.",
        })
        return false
      }

      notify({ type: NotificationType.Success, message: "Accounting Settings saved" })
      return true
    },
    [executeMutation, notify]
  )

  return { updateAccountingSettings, isUpdating: fetching }
}
